import { useState, useEffect, Fragment, useRef } from "react";
import axios from "axios";
import { Menu, Tooltip, message } from "antd";
import { FormattedMessage } from "react-intl";
import { NavLink, useLocation, useHistory } from "react-router-dom";
import { Link } from "react-router-dom";
import { VENDOR_PORTAL_ADMIN_URL } from "../../../../../constants/config";
import { shallowEqual, useSelector, useDispatch } from "react-redux";
import "../../../../../app/temp.css";
import { envFlagToBool, getSettingKey } from "../../../../../app/modules/Common/utils";
import SidebarToggleIcon from "../../../../_assets/logos/sidebar-toggle.svg?react";
import { hasAnyModulePermissions, hasPermissions, isSuperuser, Permission, PermissionGroup } from "../../../../../app/modules/Common/constants";
import * as actions from "../navSlice";
import { useSubheader } from "../../../_core/MetronicSubheader";
import {
  BarChartOutlined,
  HighlightOutlined,
  HomeOutlined,
  QuestionOutlined,
  ReconciliationOutlined,
  RocketOutlined,
  ShopOutlined,
  ShoppingOutlined,
  TabletOutlined,
  TeamOutlined,
  AuditOutlined,
  BankOutlined,
  CarOutlined,
} from "@ant-design/icons";
import { isAccountingOnboardingActivated } from "../../../../../app/modules/Accounting/_redux/onboarding/accountingOnboardingUtils";

const { SubMenu } = Menu;

const WIDE_FORM_ROUTES = [
  /^\/inventory\/purchase\/orders\/new\/?$/,
  /^\/inventory\/purchase\/order\/[^/]+\/receive\/?$/,
  /^\/inventory\/purchase\/return\/new\/?$/,
  /^\/inventory\/purchase\/order\/return\/?$/,
  /^\/inventory\/purchase\/order\/[^/]+\/return\/?$/,
];

export function AsideMenuList() {

  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [supportUnread, setSupportUnread] = useState(0);
  const [vendorPortalUnread, setVendorPortalUnread] = useState(0);
  const { currentState, sideBarState, authUser } = useSelector(
    (state) => ({ currentState: state.basicSettings, sideBarState: state.sideBar, authUser: state.auth }),
    shallowEqual
  );
  // Get auth tokens for CRM navigation (same tokens used by kapp/client)
  const authToken = useSelector((state) => state?.auth?.authToken);
  const refreshToken = useSelector((state) => state?.auth?.refreshToken);
  const { entity } = currentState;
  const [openKeys, setOpenKeys] = useState([]);

  // Fetch unread ticket count for Customer Support nav badge
  useEffect(() => {
    const CS_URL = process.env.REACT_APP_CS_PORTAL_URL || "";
    const token = authUser?.authToken;
    if (!token) return;
    let cancelled = false;
    const fetchUnread = () => {
      fetch(`${CS_URL}/cs-api/tickets/unread-count`, {
        headers: { Authorization: `Bearer ${token}` },
      })
        .then((r) => r.ok ? r.json() : null)
        .then((data) => { if (!cancelled && data) setSupportUnread(data.total_unread || 0); })
        .catch(() => {});
    };
    fetchUnread();
    const interval = setInterval(fetchUnread, 60000);
    return () => { cancelled = true; clearInterval(interval); };
  }, [authUser?.authToken]);

  // Fetch the Vendor Portal "inbox" count for the nav badge — one combined number covering
  // new reorder proposals / PO requests, new-product proposals, and unread vendor messages.
  // Gated by the same permission that gates the menu entry.
  useEffect(() => {
    const token = authUser?.authToken;
    const canSeeVendorPortal = isSuperuser() || hasPermissions([Permission.SupplierView])[0];
    if (!token || !canSeeVendorPortal) return;
    let cancelled = false;
    const fetchVendorInbox = () => {
      if (document.hidden) return;
      axios
        .get(`${VENDOR_PORTAL_ADMIN_URL}/inbox-summary`)
        .then((r) => { if (!cancelled) setVendorPortalUnread(r?.data?.data?.total || 0); })
        .catch(() => {});
    };
    fetchVendorInbox();
    const interval = setInterval(fetchVendorInbox, 60000);
    return () => { cancelled = true; clearInterval(interval); };
  }, [authUser?.authToken]);

  const dispatch = useDispatch();
  const location = useLocation();
  const history = useHistory();
  const subheader = useSubheader();

  const { setCollapsed } = sideBarState;
  const isAccountingModuleActive = isAccountingOnboardingActivated(sideBarState.accountingOnboardingStatus);
  const isDeliveryModuleEnabled = !!getSettingKey(entity, "DELIVERY", "enable_delivery_module");

  const getCurrentUrl = (location) => {
    return location.pathname.split(/[?#]/)[0];
  };

  const handleAddQuickOrderBeta = (e) => {
    e.preventDefault();
    window.location.href = `/v2/sale/sale-q-order`;
  };

  const isMenuItemActive = (url, children) => {
    const current = getCurrentUrl(location);
    if (!current || !url) {
      return false;
    }
    const currentSegments = current.split("/").filter((segment) => segment);
    const urlSegments = url.split("/").filter((segment) => segment);

    // Check if every segment in the URL matches with the current URL
    return urlSegments.every((segment, index) => segment === currentSegments[index]);
  };

  const menuItems = [
    {
      text: "Product Catalog",
      url: "/catalog",
      id: "proCatalog",
      defaultMessage: "Product Catalog",
      icon: <ShopOutlined />,
      ModulePermissionOption: PermissionGroup.CatalogModule.permissions(),
      children: [
        {
          text: "Brands",
          url: "/catalog/brands",
          id: "brands",
          defaultMessage: "Brands",
          PermissionOption: [Permission.BrandView],
        },
        {
          text: "Categories",
          url: "/catalog/categories",
          id: "categories",
          defaultMessage: "Categories",
          PermissionOption: [Permission.CategoryView],
        },
        {
          text: "Products",
          url: "/catalog/products",
          id: "products",
          defaultMessage: "Products",
          PermissionOption: [Permission.ProductView],
        },
        {
          text: "Product Groups",
          url: "/catalog/product-groups",
          id: "groups",
          defaultMessage: "Product Groups",
          PermissionOption: [Permission.ProductView],
        },
      ],
    },
    {
      text: "inventory",
      url: "/inventory",
      id: "invId1",
      defaultMessage: "Inventory",
      icon: <ReconciliationOutlined />,
      ModulePermissionOption: PermissionGroup.InventaryModule.permissions(),
      children: [
        {
          text: "purchaseOrder",
          url: "/inventory/purchase/orders",
          id: "po",
          defaultMessage: "Purchase Orders",
          PermissionOption: [Permission.PurchaseOrderView],
        },
        {
          text: "purchaseReceive",
          url: "/inventory/purchase/receives",
          id: "pr",
          defaultMessage: "Purchase Receive",
          PermissionOption: [Permission.PurchaseOrderView],
        },
        {
          text: "purchaseReturns",
          url: "/inventory/purchase/returns",
          id: "pr",
          defaultMessage: "Purchase Returns",
          PermissionOption: [Permission.PurchaseOrderView],
        },
        {
          text: "stocks",
          url: "/inventory/stock",
          id: "stocksId1",
          defaultMessage: "Stocks",
          PermissionOption: [Permission.StockView],
        },
        {
          text: "stockAlert",
          url: "/inventory/stock-alerts",
          id: "stocksId2",
          defaultMessage: "Stock Alerts",
          PermissionOption: [Permission.StockView],
        },
        {
          text: "locations",
          url: "/inventory/locations",
          id: "locationsId1",
          defaultMessage: "Stock Locations",
          PermissionOption: [Permission.LocationView],
        },
        {
          text: "stockTransfer",
          url: "/inventory/transfer/stocks",
          id: "stocksId3",
          defaultMessage: "Stock Transfers",
          PermissionOption: [Permission.ViewStockTransfer],
        },
        {
          text: "Store Channels",
          url: "/inventory/sales-stock-channels",
          id: "stocksId4",
          defaultMessage: "Store Channels",
          PermissionOption: [Permission.ChannelView],
        },
        {
          text: "Suppliers",
          url: "/inventory/suppliers",
          id: "suppId1",
          defaultMessage: "Suppliers",
          PermissionOption: [Permission.SupplierView],
        },
        {
          text: "Supplier Payments",
          url: "/inventory/supplier-payment-history",
          id: "supHis",
          defaultMessage: "Supplier Payments",
          PermissionOption: [Permission.SupplierView],
        },
        {
          text: "Supplier Portal",
          url: "/inventory/vendor-portal",
          id: "vendorPortalId1",
          defaultMessage: "Supplier Portal",
          PermissionOption: [Permission.SupplierView],
          badge: "new",
        },
        {
          text: "Konnect",
          url: "/inventory/konnect",
          id: "konnectId1",
          defaultMessage: "Konnect",
          PermissionOption: [Permission.KonnectView],
          badge: "new",
        },
      ],
    },
    {
      text: "Sale",
      url: "/sale",
      id: "sale",
      defaultMessage: "Sale",
      icon: <ShoppingOutlined />,
      ModulePermissionOption: PermissionGroup.SaleModule.permissions(),
      children: [
        {
          text: "Customers",
          url: "/sale/customers",
          id: "customers",
          defaultMessage: "Customers",
          PermissionOption: [Permission.CustomerView],
        },
        {
          text: "Customer Groups",
          url: "/sale/customer-groups",
          id: "customer-groups",
          defaultMessage: "Customer Groups",
          PermissionOption: [Permission.CustomerView],
        },
        {
          text: "Add Order",
          url: "/v2/sale/sale-q-order",
          id: "addOrderV2",
          defaultMessage: "Add Order",
          PermissionOption: [Permission.SaleOrderEdit],
          outerLink: true,
        },
        {
          text: "Fulfillments",
          url: "/sale/fulfillment",
          id: "fulfillments",
          defaultMessage: "Fulfillments",
          PermissionOption: [Permission.SaleOrderView],
        },
        {
          text: "Sales History",
          url: "/sale/history",
          id: "salesHist",
          defaultMessage: "Sales History",
          PermissionOption: [Permission.SaleOrderView],
        },
        {
          text: "Sales Return",
          url: "/sale/sale-return",
          id: "salesRet",
          defaultMessage: "Sales Return",
          PermissionOption: [Permission.SaleOrderView],
        },
        {
          text: "Payments History",
          url: "/sale/payments",
          id: "payHistory",
          defaultMessage: "Payments History",
          PermissionOption: [Permission.PaymentView],
        },
        {
          text: "Invoice History",
          url: "/sale/invoices",
          id: "invoHistory",
          defaultMessage: "Invoice History",
          PermissionOption: [Permission.SaleOrderView],
        },
        {
          text: "Abandoned Carts",
          url: "/abandon-carts",
          id: "abandoned-carts",
          defaultMessage: "Abandoned Carts",
          PermissionOption: [Permission.EcomLiveChat],
        },
      ].filter(Boolean), // Filter out any undefined or null items,
    },
    ...(isDeliveryModuleEnabled
      ? [
          {
            text: "Delivery & Shipping",
            url: "/delivery-shipping",
            id: "deliveryShipping",
            defaultMessage: "Delivery & Shipping",
            icon: <CarOutlined />,
            ModulePermissionOption: PermissionGroup.AccessDeliveryModule.permissions(),
            children: [
              {
                text: "Vehicles",
                url: "/delivery-shipping/vehicles",
                id: "vehicles",
                defaultMessage: "Vehicles",
                PermissionOption: [Permission.VehicleView],
              },
              {
                text: "Delivery",
                url: "/delivery-shipping/delivery",
                id: "delivery",
                defaultMessage: "Delivery",
                PermissionOption: [Permission.RouteView],
              },
              {
                text: "Shipping",
                url: "/delivery-shipping/shipping",
                id: "shipping",
                defaultMessage: "Shipping",
                PermissionOption: [Permission.RouteView],
              },
            ],
          },
        ]
      : []),
    {
      text: "Marketing",
      url: "/marketing",
      id: "marketing",
      defaultMessage: "Marketing",
      icon: <RocketOutlined />,
      PermissionOption: [PermissionGroup.All],
      children: [
        {
          text: "Promotions",
          url: "/marketing/promotions",
          id: "promotions",
          defaultMessage: "Promotions",
          PermissionOption: [Permission.CustomerView],
        },
        {
          text: "Whatsapp Marketing",
          url: "/marketing/whatsapp-marketing",
          id: "whatsapp marketing",
          defaultMessage: "Whatsapp Marketing",
        },
        {
          text: "KHUB AI CRM tool",
          url: "/crm",
          id: "khub ai crm",
          defaultMessage: "KHUB AI CRM tool",
          outerLink: true,
        },
      ],
    },
    {
      text: "Apps",
      url: "/apps",
      id: "apps",
      defaultMessage: "Apps",
      icon: <TabletOutlined />,
      children: [
        {
          text: "Apps",
          url: "/apps",
          id: "appsList",
          defaultMessage: "Apps",
        },
        {
          text: "Shipping Integrations",
          url: "/sale/shipping/setup",
          id: "shipIntegration",
          defaultMessage: "Shipping Integrations",
          PermissionOption: [Permission.SaleOrderView],
        },
      ],
    },
    {
      text: "Reporting",
      url: "/reporting",
      id: "reporting",
      defaultMessage: "Reporting",
      icon: <BarChartOutlined />,
      ModulePermissionOption: PermissionGroup.Reporting.permissions(),
      // ModulePermissionOption: isSuperuser(),
      children: [
        {
          text: "Business Reporting",
          url: "/reporting/purchasing-inventory",
          id: "businessRep",
          defaultMessage: "Business Reporting",
          PermissionOption: [Permission.ReportingView],
        },
        // hide for now
        // {
        //   text: "Background Process Report",
        //   url: "/reporting/background-jobs",
        //   id: "bgReport",
        //   defaultMessage: "Background Process Report",
        // },
      ],
    },
    ...(isAccountingModuleActive ? [{
       text: "Accounting",
       url: "/accounting",
       id: "accounting",
       defaultMessage: "Accounting",
       icon: <BankOutlined />,
       ModulePermissionOption: PermissionGroup.Reporting.permissions(),
       children: [
         {
           text: "Charts of Accounts",
           url: "/accounting/charts-of-accounts",
           id: "chartAccounts",
           defaultMessage: "Charts of Accounts",
           PermissionOption: [Permission.ChartOfAccountsView],
         },
         {
           text: "Expenses",
           url: "/accounting/expenses",
           id: "expenses",
           defaultMessage: "Expenses",
           PermissionOption: [Permission.ExpenseBillVendorView],
         },
         {
           text: "Bills",
           url: "/accounting/bills",
           id: "bills",
           defaultMessage: "Bills",
           PermissionOption: [Permission.ExpenseBillVendorView],
         },
         {
           text: "Bank",
           url: "/accounting/banking",
           id: "banking",
           defaultMessage: "Banking",
           PermissionOption: [Permission.BankingCreate],
         },
         {
           text: "Reconcile",
           url: "/accounting/reconciliation",
           id: "reconciliation",
           defaultMessage: "Reconcile",
           PermissionOption: [Permission.BankingCreate],
         },
         {
          text: "Journal Entries",
          url: "/accounting/journal-entries",
          id: "journalEntries",
          defaultMessage: "Journal Entries",
          PermissionOption: [Permission.ManualJournalView],
         },
         {
           text: "Deposits",
           url: "/accounting/deposits",
           id: "deposits",
           defaultMessage: "Deposits",
           PermissionOption: [Permission.BankingCreate],
         },
         {
           text: "Transfers",
           url: "/accounting/transfers",
           id: "transfers",
           defaultMessage: "Transfers",
           PermissionOption: [Permission.ExpenseBillVendorView],
         },
         {
           text: "Vendor",
           url: "/accounting/vendor",
           id: "vendor",
           defaultMessage: "Vendor",
           PermissionOption: [Permission.ExpenseBillVendorView],
         },
          {
           text: "Departments",
           url: "/accounting/departments",
           id: "departments",
           defaultMessage: "Departments",
           PermissionOption: [Permission.AccountingDepartmentView],
         },
         {
           text: "Profit & Loss",
           url: "/accounting/profit-and-loss",
           id: "profitAndLoss",
           defaultMessage: "Profit & Loss",
           // Must match the API gate (@tenant_authorization([ReportingView]) on
           // every report resource). Showing the link to users without the
           // perm just produces a 403 on click.
           PermissionOption: [Permission.BankingCreate],
         },
         {
           text: "Balance Sheet",
           url: "/accounting/balance-sheet",
           id: "balanceSheet",
           defaultMessage: "Balance Sheet",
           PermissionOption: [Permission.BankingCreate],
         },
         {
           text: "General Ledger",
           url: "/accounting/general-ledger",
           id: "generalLedger",
           defaultMessage: "General Ledger",
           PermissionOption: [Permission.BankingCreate],
         },
         {
           text: "Trial Balance",
           url: "/accounting/trial-balance",
           id: "trialBalance",
           defaultMessage: "Trial Balance",
           PermissionOption: [Permission.BankingCreate],
         },
         {
           text: "Inventory Reconciliation",
           url: "/accounting/inventory-reconciliation",
           id: "inventoryReconciliation",
           defaultMessage: "Inventory Reconciliation",
           PermissionOption: [Permission.BankingCreate],
         },
       ],
     }] : []),
    {
      text: "Manage Themes",
      url: "/store",
      id: "Themes",
      defaultMessage: "Manage Themes",
      icon: <HighlightOutlined />,
      // ModulePermissionOption: isSuperuser(),
      children: [
        {
          text: "Mobile Application",
          url: "/store/apptheme",
          id: "mobApp",
          defaultMessage: "Mobile Application",
        },
        {
          text: "Ecom site Themes",
          url: "/store/ecomtheme",
          id: "ecomApp",
          defaultMessage: "Ecom site Themes",
        },
        // {
        //   text: "Web Application",
        //   url: "/store/theme",
        //   id: "webApp",
        //   defaultMessage: "Web Application",
        // },
      ],
    },
    {
      text: "Manage Custom Pages",
      url: "/custom-pages",
      id: "customPageId1",
      defaultMessage: "Manage Custom Pages",
      icon: <AuditOutlined />,
      // ModulePermissionOption: isSuperuser(),
      children: [
        {
          text: "Custom Pages",
          url: "/custom-pages/list",
          id: "customPageId2",
          defaultMessage: "Custom Pages",
        },
        //  commenting for now as per Umar Ayub Suggestions
        // {
        //   text: "Create Custom Page",
        //   url: "/custom-pages/new",
        //   id: "customPage2",
        //   defaultMessage: "Create Custom Page",
        // },
      ],
    },
    {
      text: "Queries",
      id: "queries",
      defaultMessage: "Queries",
      icon: <QuestionOutlined />,
      // ModulePermissionOption: isSuperuser(),
      children: [
        {
          text: "Queries",
          url: "/queries/list",
          id: "queriesList",
          defaultMessage: "Queries",
        },
        {
          text: "Customer Support",
          url: "/support",
          id: "customerSupport",
          defaultMessage: "Customer Support",
        },
      ],
    },
    {
      text: "Users",
      url: "/users",
      id: "usersId1",
      defaultMessage: "Users",
      // ModulePermissionOption: isSuperuser(),
      icon: <TeamOutlined />,
      children: [
        {
          text: "Users",
          url: "/users/user",
          id: "usersId2",
          defaultMessage: "Users",
        },
        {
          text: "Permissions",
          url: "/users/permissions",
          id: "perId",
          defaultMessage: "Permissions",
        },
        {
          text: "Roles",
          url: "/users/roles",
          id: "rolesId",
          defaultMessage: "Roles",
        },
      ],
    },
  ];

  if (hasPermissions([Permission.DashboardView])[0]) {
    menuItems.unshift({
      text: "Dashboard",
      url: "/dashboard",
      id: "dashboard",
      defaultMessage: "Dashboard",
      icon: <HomeOutlined />,
      ModulePermissionOption: [Permission.DashboardView],
    });
  }

  const handleSubMenuOpenChange = (keys) => {
   const latestOpenKey = keys.find((key) => !openKeys.includes(key));
   setOpenKeys(latestOpenKey ? [latestOpenKey] : []);
  };

  const getMenuItemKey = (item) => item.url || item.id;
  const pendingHeaderRef = useRef(null);

  useEffect(() => {
    if (setCollapsed) {
      return;
    }

    const rootPath = location.pathname.split("/").filter(Boolean)[0];
    setOpenKeys(rootPath ? [`/${rootPath}`] : []);
  }, [location.pathname, setCollapsed]);

  const setHeader = (parent, child) => {
    pendingHeaderRef.current = { parent, child };
    return null;
  };

  // Only re-derive/apply the sidebar's generic title+breadcrumb when the active route actually
  // changes, not on every re-render of this component (redux/context updates elsewhere used to
  // re-run this unconditionally, repeatedly clobbering a more specific title a page had already
  // set itself — e.g. PurchaseOrdersPage's "Add Purchase Order" — which is why that page had to
  // fight back with a polling setInterval reassert; that workaround is no longer needed with this
  // effect scoped to real navigations).
  useEffect(() => {
    if (pendingHeaderRef.current) {
      const { parent, child } = pendingHeaderRef.current;
      pendingHeaderRef.current = null;
      const { url, defaultMessage } = parent;
      const childTitle = child?.defaultMessage;
      const title = childTitle || defaultMessage;
      if (subheader.title !== title) {
        const breadcrumbs = [{ title: defaultMessage, pathname: url }];
        childTitle && breadcrumbs.push({ title: childTitle });
        subheader.setBreadcrumbs(breadcrumbs);
        subheader.setTitle(title);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname]);

  // Wide create forms (new PO, receive a PO, new purchase return incl. from a PO) get the full width: collapse the
  // sidebar when one opens. Only on arrival, so the user can still expand it while on the page.
  useEffect(() => {
    if (!setCollapsed && WIDE_FORM_ROUTES.some((route) => route.test(location.pathname))) {
      dispatch(actions.setCollapsed(true));
      setOpenKeys([]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname]);

  const handleCollapseToggle = () => {
    const newCollapseState = !setCollapsed;
    dispatch(actions.setCollapsed(newCollapseState));
    setOpenKeys([]); // Close all SubMenus when sidebar is collapsed
  };

  const handleMenuItemClick = ({ item, key, keyPath, domEvent }) => {
    if (setCollapsed) {
      if (domEvent.target.closest(".svg_icon")) {
        // Check if the clicked element or its parent is an icon
        handleCollapseToggle();
      }
    }
  };
  const handleSubMenuTitleClick = (info) => {
    if (setCollapsed) {
      handleCollapseToggle();
    }
  };

  const renderMenuItems = (items) => {
    return (
      <>
        {/* comment for future use */}
        {/* <button
                    className="sidebar-toggle-button"
                    onClick={() => setIsSidebarOpen(!isSidebarOpen)}>
                    Toggle Sidebar
                </button> */}
        <div className={`sidebar ${isSidebarOpen ? "active" : ""} ${setCollapsed ? "collapsed" : ""}`} style={{ backgroundColor: "#050505" }} id="newAsideMenu">
          <div className="logo-toggle-container">
            <div className={setCollapsed ? "collapsed-brand-logo-container" : "brand-logo-container"}>
              {!setCollapsed && (
                <Link to="" className="brand-logo">
                  <img alt="Logo" src={getSettingKey(entity, "Basic", "dashboard_logo")} />
                </Link>
              )}
            </div>
            <Tooltip title={setCollapsed ? "Expand sidebar" : "Collapse sidebar"} placement="right">
              <button
                type="button"
                className={`toggle-icon ${setCollapsed ? "toggle-icon--collapsed" : ""}`}
                onClick={handleCollapseToggle}
                aria-label={setCollapsed ? "Expand sidebar" : "Collapse sidebar"}
              >
                <SidebarToggleIcon aria-hidden="true" />
              </button>
            </Tooltip>
          </div>
          {/* Native scrolling: PerfectScrollbar's rails padded the scroll height, letting the menu
              scroll past its last item into blank space once a SubMenu expanded/collapsed. */}
          <div className="aside-menu-scroll">
            <Menu
              className={setCollapsed ? "customMenuToggle" : "customMenu"}
              mode="inline"
              theme="dark"
              selectedKeys={[location.pathname]}
              onClick={handleMenuItemClick}
              openKeys={setCollapsed ? [] : openKeys} // if setCollapsed is true, then don't open any SubMenu
              onOpenChange={handleSubMenuOpenChange}
            >
              {menuItems.map((item, index) => (
                <Fragment key={index}>
                  {item.children && item.children.length > 0 && (isSuperuser() || hasAnyModulePermissions(item.ModulePermissionOption)) ? (
                    <SubMenu
                      key={getMenuItemKey(item)}
                      title={
                        setCollapsed ? (
                          <Tooltip
                            title={
                              <span>
                                <FormattedMessage id={item.id} defaultMessage={item.defaultMessage} />
                              </span>
                            }
                            placement="right"
                          >
                            <span className={`${isMenuItemActive(item.url, item.children) ? "active-main-item" : "inactive-item"} svg-icons`}>
                              {item?.icon}
                            </span>
                          </Tooltip>
                        ) : (
                          <span
                            id={item.defaultMessage}
                            className={`${isMenuItemActive(item.url, item.children) ? "active-main-item" : "inactive-item"} primary-font content-height`}
                          >
                            <span className="svg_icon">{item?.icon}</span>
                            <Tooltip title={<FormattedMessage id={item.id} defaultMessage={item.defaultMessage} />} placement="right">
                              <span>
                                <FormattedMessage id={item.id} defaultMessage={item.defaultMessage} />
                              </span>
                            </Tooltip>
                            {item.id === "queries" && supportUnread > 0 && (
                              <span className="sidebar-unread-count">{supportUnread}</span>
                            )}
                          </span>
                        )
                      }
                      onTitleClick={handleSubMenuTitleClick}
                      openKeys={openKeys} // Control which SubMenu is open
                      onOpenChange={handleSubMenuOpenChange} // Handle SubMenu open changes
                    >
                      {item.children.map((child, childIndex) => {
                        isMenuItemActive(child.url) && setHeader(item, child);
                        return (
                          ((!child?.PermissionOption && isSuperuser()) || hasPermissions(child.PermissionOption)[0]) && (
                            <Menu.Item
                              key={child.url}
                              className={`${isMenuItemActive(child.url) ? "active-sub-item" : "inactive-item"} primary-font content-height`}
                            >
                              <NavLink
                                to={child.url}
                                exact={true}
                                onClick={(e) => {
                                  if (child.outerLink) {
                                    if (child.id === "addOrderV2") {
                                      handleAddQuickOrderBeta(e);
                                      return;
                                    }

                                    window.location.href = child.url;
                                    e.preventDefault();
                                  }
                                }}
                              >
                                <i className="menu-bullet">
                                  <span>&#8226;</span>
                                </i>
                                <span></span>
                                <FormattedMessage id={child.id} defaultMessage={child.defaultMessage} />
                                {child.id === "customerSupport" && supportUnread > 0 && (
                                  <span className="sidebar-unread-count">{supportUnread}</span>
                                )}
                                {child.id === "vendorPortalId1" && vendorPortalUnread > 0 && (
                                  <span className="sidebar-unread-count" style={{ color: "#fff" }}>{vendorPortalUnread}</span>
                                )}
                                {child.badge && (
                                  <span className="label label-light-primary label-inline font-weight-bold ml-2" style={{ fontSize: 10 }}>
                                    {child.badge}
                                  </span>
                                )}
                              </NavLink>
                            </Menu.Item>
                          )
                        );
                      })}
                    </SubMenu>
                  ) : (
                    (isSuperuser() || hasAnyModulePermissions(item.ModulePermissionOption)) && (
                      <Menu.Item
                        key={item.url}
                        icon={
                          setCollapsed ? (
                            <Tooltip title={<FormattedMessage id={item.id} defaultMessage={item.defaultMessage} />} placement="right">
                              <span className="svg_icon">{item?.icon}</span>
                            </Tooltip>
                          ) : (
                            <span className="svg_icon">{item?.icon}</span>
                          )
                        }
                        className={`${isMenuItemActive(item.url) ? "active-main-item" : "inactive-item"} primary-font content-height`}
                      >
                        {isMenuItemActive(item.url) && setHeader(item)}
                        {!setCollapsed && (
                          <NavLink to={item.url}>
                            <FormattedMessage id={item.id} defaultMessage={item.defaultMessage} />
                          </NavLink>
                        )}
                      </Menu.Item>
                    )
                  )}
                </Fragment>
              ))}
            </Menu>
          </div>
        </div>
      </>
    );
  };

  return <>{renderMenuItems(menuItems)}</>;
}
